import React, { useState } from 'react';
import { Layout, Menu } from '@duxton-web/ui';
import { capitalize } from 'lodash';
import { Link } from 'react-router-dom';

import { DomainFamily } from '@/common/domains';
import { domainBaseEnvironment, isProduction } from '@/common/environment';
import { isMoveITTenant } from '@/common/Tenants';
import { GRABX_SLACK_CHANNEL } from '@/constants';
import { isAdmin } from '@/utils/user';

import '@/features/layout/main-menu/index.css';

import MainMenuCollapse from '@/features/layout/main-menu-collapse';
import MainMenuItem from '@/features/layout/main-menu-item';
import Profile from '@/features/layout/profile';

export const LEFT_LAYOUT_SIDER_WIDTH: number = 200;

interface MainMenuProps {
  selectedKey?: string;
  domainFamily: string;
  onLogout: () => void;
}

const EnvironmentIndicatorStyle = {
  default: {
    background: 'rgba(240, 152, 0, 0.1)',
    color: '#F09800',
  },
  prod: {
    background: 'rgba(0, 177, 79, 0.1)',
    color: '#00B14F',
  },
};

const MainMenu = ({
  selectedKey = 'experiments',
  domainFamily,
  onLogout,
}: MainMenuProps) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);

  function onCollapse(collapsed: boolean): void {
    setCollapsed(collapsed);
  }

  function toggle(): void {
    setCollapsed(!collapsed);
  }

  return (
    <Layout.Sider
      theme="light"
      collapsible={true}
      collapsed={collapsed}
      onCollapse={onCollapse}
      className="main-layout-slider"
      trigger={null}
      width={280}
      collapsedWidth={76}
      style={{
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {isMoveITTenant() && (
          <div className="main-menu-logo">
            <Link to={`/${domainFamily}`}>
              <img data-testid="moveit-logo" src="/moveit.png" />
            </Link>
          </div>
        )}
        {collapsed ? (
          <div className="main-menu-logo-collapsed">
            <Link to={`/${domainFamily}`}>
              <div className="main-menu-logo-collapsed__container">
                <img
                  data-testid="grab-collapsed-logo"
                  src="/grab-g-letter.svg"
                />
              </div>
            </Link>
          </div>
        ) : (
          <div className="main-menu-logo">
            <Link to={`/${domainFamily}`}>
              <div className="main-menu-logo__container">
                <img data-testid="grab-logo" src="/grab.svg" />
              </div>
            </Link>
          </div>
        )}
        <Menu
          theme="light"
          selectedKeys={[selectedKey]}
          mode="inline"
          style={{
            paddingLeft: 12,
            paddingRight: 12,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderInlineEnd: 'none',
          }}
        >
          <MainMenuItem
            to={`/${domainFamily}/experiments`}
            key="experiments"
            title="Experiments"
            icon="experiment"
            selected={selectedKey === 'experiments'}
          />
          {domainFamily === DomainFamily.TRANSPORT && (
            <MainMenuItem
              to={`/${domainFamily}/variables`}
              key="variables"
              icon="variables"
              title="Variables"
              selected={selectedKey === 'variables'}
            />
          )}
          <MainMenuItem
            to={`/${domainFamily}/groups`}
            key="groups"
            icon="groups"
            title="Groups"
            selected={selectedKey === 'groups'}
          />
          {isAdmin() ? (
            <MainMenuItem
              to={`/${domainFamily}/admin`}
              key="admin"
              icon="admin"
              title="Admin"
              selected={selectedKey === 'admin'}
            />
          ) : null}

          <MainMenuItem
            key="env"
            icon="browser"
            title={`${capitalize(domainBaseEnvironment)} v${process.env.WEB_APP_VERSION}`}
            style={{
              ...(isProduction
                ? EnvironmentIndicatorStyle.prod
                : EnvironmentIndicatorStyle.default),
              marginTop: 'auto',
            }}
            linkStyle={{ cursor: 'default' }}
            action={() => {}}
          />
          <MainMenuItem
            key="feedback"
            icon="feedback"
            title="Give Feedback"
            to={GRABX_SLACK_CHANNEL}
          />
        </Menu>
        <Profile
          domainFamily={domainFamily}
          onLogout={onLogout}
          collapsed={collapsed}
        />
        <MainMenuCollapse collapsed={collapsed} toggle={toggle} />
      </div>
    </Layout.Sider>
  );
};

export default MainMenu;
